import { useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { io } from 'socket.io-client'
import { getAuthTokenFromCookie } from 'helpers/cookie.helpers'

/**
 * Generic socket connection for any arcade game namespace.
 *
 * Replaces the copy-pasted connect block that used to live in every
 * use<Game>GameSocketNameSpace hook. Connecting to a game namespace is enough
 * for the backend to track presence (Player Online admin page).
 *
 * - Presence-only games: just call useGameSocketNamespace(MINE_GAME).
 * - Games with events: use the returned `on` helper to subscribe.
 *
 * Notes:
 * - transports polling + upgrade:false — websocket-only causes a long connect
 *   delay against the current user-api socket.io version.
 * - The socket is created inside useEffect and only once isLoggedIn is true,
 *   otherwise the token isn't set yet and auth sends "Bearer null".
 *
 * @param {string} namespace socket.io namespace path, e.g. '/mine-game'
 * @param {object} [options]
 * @param {object} [options.query] extra handshake query, e.g. { gameId: 1 }
 */
const useGameSocketNamespace = (namespace, options = {}) => {
  const { query } = options
  const socketRef = useRef(null)
  const { isLoggedIn } = useSelector((state) => state.auth)

  // keep a stable dep for the effect even when a new object is passed each render
  const querySignature = query ? JSON.stringify(query) : ''

  useEffect(() => {
    if (!isLoggedIn || !namespace) return

    const token = getAuthTokenFromCookie()
    if (!token) return

    // drop any stale connection first
    if (socketRef.current) socketRef.current.disconnect()

    socketRef.current = io(`${process.env.REACT_APP_BACKEND_URL_WS}${namespace}`, {
      transports: ['polling'],
      upgrade: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: true,
      ...(querySignature ? { query: JSON.parse(querySignature) } : {}),
      auth: {
        authorization: `Bearer ${token}`
      }
    })

    socketRef.current.on('connect_error', (error) => {
      console.error(`${namespace} socket connection error:`, error.message)
    })

    socketRef.current.on('error', (error) => {
      console.error(`${namespace} socket error:`, error)
    })

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [isLoggedIn, namespace, querySignature])

  /**
   * Subscribe to an event. Returns an unsubscribe function.
   * Safe to call before the socket exists (it just won't attach), so callers
   * that need guaranteed delivery should call it from an effect.
   */
  const on = useCallback((event, cb) => {
    if (!cb) return () => {}
    socketRef.current?.on(event, cb)
    return () => socketRef.current?.off(event, cb)
  }, [])

  /** Remove a listener (or all listeners for an event when cb is omitted). */
  const off = useCallback((event, cb) => {
    socketRef.current?.off(event, cb)
  }, [])

  /** Emit an event to the server. */
  const emit = useCallback((event, payload) => {
    socketRef.current?.emit(event, payload)
  }, [])

  return { socket: socketRef, on, off, emit }
}

export default useGameSocketNamespace
